import React, { useEffect, useRef } from "react";
import Icons from "../icons";

/* ------------------------------------------------------------------ */
/*  Error dialog                                                        */
/*                                                                     */
/*  Something could not be done and there is nothing to confirm: same   */
/*  shape as ConfirmModal, but a red header and only Cancel. Escape or  */
/*  the backdrop closes it too.                                         */
/* ------------------------------------------------------------------ */
export default function ErrorDialog({ title, message, onClose, icon: BadgeIcon = Icons.Alert }) {
  const cancelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-message"
        className="relative z-10 w-full max-w-xl overflow-hidden rounded-md bg-card shadow-2xl"
      >
        {/* Header — red: this is a refusal, not a question */}
        <div className="flex items-center gap-2 bg-rose-600 px-5 py-3.5">
          <h3 id="error-dialog-title" className="flex-1 text-base font-semibold tracking-tight text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-white/70 transition hover:bg-white/15 hover:text-white"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-4 px-5 py-5">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-rose-50 text-rose-600">
            <BadgeIcon className="h-5 w-5" />
          </span>
          <div id="error-dialog-message" className="min-w-0 flex-1 pt-1.5 text-sm leading-relaxed text-slate-600">
            {message}
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3.5">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-card px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
